'use client'

import { type Account, datadogRum, type User } from '@datadog/browser-rum'
import { addNextjsError, nextjsPlugin } from '@datadog/browser-rum-nextjs'

import {
  redactSensitiveString,
  sanitizeLogValue,
  sanitizeUrl,
  truncate,
} from './core'

type AllowedTracingUrl = {
  match: (url: string) => boolean
  propagatorTypes: Array<'tracecontext'>
}

export type RumSanitizableEvent = {
  type: string
  view?: { url?: string }
  resource?: { url?: string }
  error?: { message?: unknown; stack?: unknown }
  context?: Record<string, unknown>
}

const EXTENSION_OBJECT_NOT_FOUND_PATTERN =
  /^Uncaught "Object Not Found Matching Id:\d+, MethodName:update, ParamCount:4"$/

function normalizePrefix(prefix: string): string | null {
  try {
    const url = new URL(prefix)
    const path = url.pathname.replace(/\/$/, '')
    return `${url.origin}${path}`
  } catch {
    return null
  }
}

function matchesPrefix(url: string, prefix: string): boolean {
  return url === prefix || url.startsWith(`${prefix}/`)
}

function isBrewVercelHost(hostname: string): boolean {
  return (
    hostname.endsWith('.vercel.app') &&
    (hostname.startsWith('brew-omni-agent-mvp-') ||
      hostname.startsWith('brew-admin-'))
  )
}

function isOwnedTracingPrefix(prefix: string, origin: string): boolean {
  try {
    const candidate = new URL(prefix)
    const current = new URL(origin)
    return (
      candidate.origin === current.origin ||
      candidate.hostname === 'brew.new' ||
      candidate.hostname.endsWith('.brew.new') ||
      isBrewVercelHost(candidate.hostname)
    )
  } catch {
    return false
  }
}

export function buildAllowedTracingUrls(args: {
  origin: string
  additionalPrefixes?: Array<string>
}): Array<AllowedTracingUrl> {
  const prefixes = [args.origin, ...(args.additionalPrefixes ?? [])]
    .map((prefix) => normalizePrefix(prefix.trim()))
    .filter((prefix): prefix is string => Boolean(prefix))
    .filter((prefix) => isOwnedTracingPrefix(prefix, args.origin))

  return Array.from(new Set(prefixes)).map((prefix) => ({
    match: (url: string) => matchesPrefix(url, prefix),
    propagatorTypes: ['tracecontext'],
  }))
}

function isKnownRumNoise(event: RumSanitizableEvent): boolean {
  if (event.type !== 'error' || typeof event.error?.message !== 'string') {
    return false
  }
  return (
    event.error.message.includes('ResizeObserver loop') ||
    EXTENSION_OBJECT_NOT_FOUND_PATTERN.test(event.error.message)
  )
}

export function sanitizeRumEvent(event: RumSanitizableEvent): boolean {
  if (isKnownRumNoise(event)) {
    return false
  }
  if (typeof event.view?.url === 'string') {
    event.view.url = sanitizeUrl(event.view.url)
  }
  if (typeof event.resource?.url === 'string') {
    event.resource.url = sanitizeUrl(event.resource.url)
  }
  if (typeof event.error?.message === 'string') {
    event.error.message = truncate(
      redactSensitiveString(event.error.message),
      2000
    )
  }
  if (typeof event.error?.stack === 'string') {
    event.error.stack = truncate(
      redactSensitiveString(event.error.stack),
      16_384
    )
  }
  if (event.context) {
    event.context = sanitizeLogValue(event.context) as Record<string, unknown>
  }
  return true
}

/**
 * Parse one `NEXT_PUBLIC_DATADOG_*_SAMPLE_RATE` value.
 *
 * Unset, blank and non-numeric values mean "use the profile default", and
 * numbers are clamped to the SDK's 0–100 range. `Number('')` is `0`, so a
 * variable that merely existed with an empty value used to switch the whole
 * RUM profile off while `initDatadogRum` still reported success. An explicit
 * `'0'` is honoured: that is an operator decision, not a misconfiguration.
 */
export function parseSampleRate(
  value: string | number | null | undefined,
  fallback: number
): number {
  if (value === undefined || value === null) {
    return fallback
  }
  const raw = typeof value === 'string' ? value.trim() : value
  if (raw === '') {
    return fallback
  }
  const parsed = typeof raw === 'number' ? raw : Number(raw)
  if (!Number.isFinite(parsed)) {
    return fallback
  }
  return Math.min(100, Math.max(0, parsed))
}

export function initDatadogRum(args: {
  applicationId: string | undefined
  clientToken: string | undefined
  service: string
  env: string
  version?: string
  site?: string
  origin: string
  additionalTracingPrefixes?: Array<string>
  sessionSampleRate?: string | number
  replaySampleRate?: string | number
  profilingSampleRate?: string | number
}): boolean {
  if (!args.applicationId?.trim() || !args.clientToken?.trim()) {
    return false
  }

  datadogRum.init({
    applicationId: args.applicationId,
    clientToken: args.clientToken,
    site: args.site ?? 'datadoghq.com',
    service: args.service,
    env: args.env,
    version: args.version,
    sessionSampleRate: parseSampleRate(args.sessionSampleRate, 100),
    sessionReplaySampleRate: parseSampleRate(args.replaySampleRate, 20),
    // JS self-profiling is Chromium-only and additionally gated on the
    // `Document-Policy: js-profiling` response header both Next configs send.
    profilingSampleRate: parseSampleRate(args.profilingSampleRate, 5),
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    compressIntakeRequests: true,
    defaultPrivacyLevel: 'mask-user-input',
    traceContextInjection: 'sampled',
    propagateTraceBaggage: false,
    allowedTracingUrls: buildAllowedTracingUrls({
      origin: args.origin,
      additionalPrefixes: args.additionalTracingPrefixes,
    }),
    plugins: [nextjsPlugin()],
    beforeSend: (event) => sanitizeRumEvent(event as RumSanitizableEvent),
  })
  return true
}

export function setRumIdentity(args: {
  user: User & { id: string }
  account?: Account & { id: string }
}): void {
  datadogRum.setUser(args.user)
  if (args.account) {
    datadogRum.setAccount(args.account)
  } else {
    datadogRum.clearAccount()
  }
}

export function clearRumIdentity(): void {
  datadogRum.clearUser()
  datadogRum.clearAccount()
}

export function addRumAction(
  name: string,
  context?: Record<string, unknown>
): void {
  datadogRum.addAction(
    name,
    sanitizeLogValue(context ?? {}) as Record<string, unknown>
  )
}

export function addRumError(
  error: unknown,
  context?: Record<string, unknown>
): void {
  datadogRum.addError(
    error,
    sanitizeLogValue(context ?? {}) as Record<string, unknown>
  )
}

export function addRumNextjsError(error: Error & { digest?: string }): void {
  addNextjsError(error)
}
